// Product contacts (the team members a product can be assigned to) come from
// GET /product-contacts, backed by the `product_contacts` table, so the list
// changes without a deploy. Loaded once per session and cached for the
// forms' pickers and the grids' singleSelect columns.
import { useEffect, useState } from "react";
import axios from "./dvdlocator";

export interface ProductContact {
  id: number;
  name: string;
  active: boolean;
}

let cachedNames: string[] = [];
let pending: Promise<string[]> | null = null;
const listeners = new Set<(names: string[]) => void>();

export async function fetchProductContacts(): Promise<ProductContact[]> {
  const res = await axios.get("/product-contacts");
  return res.data.contacts;
}

/** The active contact names, once loaded; [] before the first load. */
export function productContactNames(): string[] {
  return cachedNames;
}

/** Loads the list once per session (shared by every caller). */
export function loadProductContacts(): Promise<string[]> {
  if (!pending) {
    pending = fetchProductContacts()
      .then((contacts) => {
        cachedNames = contacts.filter((c) => c.active).map((c) => c.name);
        listeners.forEach((listener) => listener(cachedNames));
        return cachedNames;
      })
      .catch((error) => {
        // Forms still render: their pickers offer the row's current contact
        // and the logged-in user. Retry on the next call.
        console.error("Error loading product contacts:", error);
        pending = null;
        return cachedNames;
      });
  }
  return pending;
}

/** Test hook: forget the cached list. */
export function resetProductContactsCache() {
  cachedNames = [];
  pending = null;
}

/** The active contact names, loading them on first use. */
export function useProductContacts(): string[] {
  const [names, setNames] = useState<string[]>(cachedNames);
  useEffect(() => {
    listeners.add(setNames);
    loadProductContacts().then(setNames);
    return () => {
      listeners.delete(setNames);
    };
  }, []);
  return names;
}
